import * as React from 'react';
import {useSyncExternalStore} from 'react';
import {weakMemo} from '@packages/utilities/memo';

type Listener = () => void;
type Unsubscribe = () => void;

type SmartContextStore<a> = {
  getSnapshot: () => a;
  subscribe: (listener: Listener) => Unsubscribe;
  setState: (nextState: a) => void;
};

const invokeListener = (listener: Listener) => listener();

function createContextStore<a>(state: a): SmartContextStore<a> {
  const listeners = new Map<Listener, Listener>();
  let currentState = state;

  return {
    getSnapshot: () => currentState,
    subscribe: (listener: Listener) => {
      listeners.set(listener, listener);

      return () => {
        listeners.delete(listener);
      };
    },
    setState: (nextState: a) => {
      currentState = nextState;
      listeners.forEach(invokeListener);
    },
  };
}

const createCallback = weakMemo(<a,>(store: SmartContextStore<a>) =>
  weakMemo(<b,>(selector: (context: a) => b) => {
    return () => selector(store.getSnapshot());
  })
);

type ContextProviderProps<a> = React.PropsWithChildren<{
  value: a;
}>;

const getDerivedStateFromProps = <a,>(
  nextProps: ContextProviderProps<a>,
  state: {store: SmartContextStore<a>}
) => {
  const {store} = state;
  const {value} = nextProps;
  if (store.getSnapshot() !== value) {
    store.setState(value);
  }
  return null;
};

export const createSmartContext = <a,>(defaultContext: a) => {
  const Context = React.createContext<SmartContextStore<a>>(
    createContextStore(defaultContext)
  );

  class ContextProvider extends React.Component<ContextProviderProps<a>> {
    static getDerivedStateFromProps = getDerivedStateFromProps;
    state = {
      store: createContextStore(this.props.value),
    };
    render() {
      return (
        <Context.Provider value={this.state.store}>
          {this.props.children}
        </Context.Provider>
      );
    }
  }

  const useContext = <b,>(selector: (context: a) => b) => {
    const context = React.useContext(Context);
    const callback = createCallback(context)(selector);
    return useSyncExternalStore(context.subscribe, callback, callback);
  };

  return {ContextProvider, useContext, Context};
};
